import React from 'react';
import { H1, H4 } from '../elements/Text';
import {
  StepScrollWrapper,
  StepScreen,
  StepWrapper,
  // EmptyDiv,
  SectionsWrapper,
  SectionSnap,
  ContentWrapper,
  ContentContainer,
} from './common';
import BottomActionBar from './BottomNavigation';
import TextSection from './Sections/TextSection';
import TextInputSection from './Sections/TextInputSection';
import RankingSection from './Sections/RankingSection';
import MultipleChoiceSection from './Sections/MultipleChoiceSection';
import MatchingSection from './Sections/MatchingSection';
import FillInTheGapSection from './Sections/FillInTheGapSection';
import PythonPlaygroundSection from './Sections/PythonPlaygroundSection';
import LLMPlaygroundSection from './Sections/LLMPlaygroundSection';
import { STEP_CONTAINER_CLASSNAME } from '../../hooks/assessment-test/use-step-scroll-state';
import SideNavigation from './SideNavigation';
import MultipleRatingSection from './Sections/MultipleRatingSection';
import styled from 'styled-components';
import { AssessmentTestContextType } from '../../contexts/assessment-test';
import { useActiveTestStep } from '../../hooks/assessment-test/use-active-step';
import { getSectionClassName } from '../../contexts/assessment-test/helpers';
import { INTRODUCTION_SECTION_ID } from '../../contexts/assessment-test/reducers/update-step-section';
import TestTimeoutHandler from './TestTimeoutHandler';
import PythonEditorSection from './Sections/PythonEditorSection';
import LLMEditorSection from './Sections/LLMEditorSection';
import TextTemplateSection from './Sections/TextTemplateSection';
import MatrixRankingSection from './Sections/MatrixRankingSection';

export default function Step() {
  const { stepId } = useActiveTestStep();

  return (
    <>
      <TestTimeoutHandler />
      <StepScreen>
        <StepWrapper className={STEP_CONTAINER_CLASSNAME}>
          <StepScrollWrapper key={stepId}>
            <SideNavigation />
            <ContentWrapper data-scroll-container="true">
              <ContentContainer>
                <SectionsWrapper>
                  <Title />
                  <IntroductionSection />
                  <Sections />
                  {/* <EmptyDiv height={300} /> */}
                </SectionsWrapper>
              </ContentContainer>
            </ContentWrapper>
          </StepScrollWrapper>
        </StepWrapper>
        <BottomActionBar />
      </StepScreen>
    </>
  );
}

function Title() {
  const { step } = useActiveTestStep();

  return (
    <SectionSnap>
      <StepLabel>{step.label}</StepLabel>
      <H1 style={{ marginTop: 16 }}>{step.title}</H1>
    </SectionSnap>
  );
}

const StepLabel = styled.div`
  font-size: 25px;
  font-weight: 600;
  margin-top: 72px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

function IntroductionSection() {
  const { step } = useActiveTestStep();

  if (!step.introduction) {
    return null;
  }
  return (
    <SectionSnap
      className={getSectionClassName(step.id, INTRODUCTION_SECTION_ID)}
      id={INTRODUCTION_SECTION_ID}
    >
      <TextSection
        content={step.introduction.text}
        style={{
          minHeight: 'unset',
          marginBottom: 0,
        }}
      />
    </SectionSnap>
  );
}

function Sections() {
  const { step } = useActiveTestStep();

  return (
    <>
      {step?.sections.map((section) => {
        return (
          <React.Fragment key={`section-fragment-${section.id}`}>
            <SectionSnap
              className={getSectionClassName(step.id, section.id)}
              key={section.id}
              id={section.id}
              style={{
                minHeight: '80vh',
              }}
            >
              <H4 style={{ fontWeight: 600, marginBottom: 0 }}>
                {section.title}
              </H4>
              <SectionRenderer
                key={section.id}
                section={section}
                stepId={step.id}
              />
            </SectionSnap>
          </React.Fragment>
        );
      })}
    </>
  );
}

function SectionRenderer({
  section,
  stepId,
}: {
  section: AssessmentTestContextType['steps'][number]['sections'][number];
  stepId: string;
}) {
  switch (section.type) {
    case 'text-input': {
      return (
        <TextInputSection key={section.id} section={section} stepId={stepId} />
      );
    }
    case 'ranking': {
      return (
        <RankingSection key={section.id} section={section} stepId={stepId} />
      );
    }
    case 'multiple-choice': {
      return (
        <MultipleChoiceSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }
    case 'matching': {
      return (
        <MatchingSection key={section.id} section={section} stepId={stepId} />
      );
    }
    case 'fill-in-the-gap': {
      return (
        <FillInTheGapSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }
    case 'text-template': {
      return (
        <TextTemplateSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }
    case 'py-playground': {
      return (
        <PythonPlaygroundSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }
    case 'llm-playground': {
      return (
        <LLMPlaygroundSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }
    case 'multiple-rating': {
      return (
        <MultipleRatingSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }
    case 'py-editor': {
      return (
        <PythonEditorSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }
    case 'llm-editor': {
      return (
        <LLMEditorSection key={section.id} section={section} stepId={stepId} />
      );
    }
    case 'matrix-ranking': {
      return (
        <MatrixRankingSection
          key={section.id}
          section={section}
          stepId={stepId}
        />
      );
    }

    default: {
      throw new Error(`Unknown section type`);
    }
  }
}
